import {
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
  GridOptions,
  DefaultMenuItem,
  ValueFormatterParams
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Checkbox, Tooltip } from "antd";
import NoLinkIcon from "components/svg-icons/no-link-icon";
import { ScheduleTaskType } from "pages/submittal-schedule-linking/models";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ExpandIconPath from "assets/svg/expand-icon.svg";
import CollapseIconPath from "assets/svg/collapse-icon.svg";
import { GridLoadingIndicator } from "components/widgets";
import { DateFilter, DateUtils } from "utils/dateutils";
import { LinkTypes } from "entity-app/constants";
import CiqAgSearch from "components/ciq-ag-search";
import {
  criticalActivityHeaderComponent,
  criticalActivityIndicatorCellRenderer
} from "components/cell-renders";
import { ECustomUserTypes, GanttActivityTypes } from "constants/index";
import {
  getScheduleFileExtension,
  matchCustomUserProjectRole
} from "utils/utils";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";

function CellWithDateCheckbox(params: any) {
  const { data, isStartDate, context } = params;

  const { onCheckBoxAction, linkedActivities } = context;

  if (isStartDate) {
    const startDate = data?.start_date
      ? DateUtils.format(data.start_date, "MM-DD-YYYY")
      : "";

    if (startDate === "") return "";

    const isLinkedByF2S = linkedActivities.some(
      (activity: any) =>
        activity.id === data.id &&
        activity.link_type === LinkTypes.finish_to_start
    );

    return (
      <div className="flex w-full justify-end items-center">
        {data.type !== 5 ? (
          <Checkbox
            checked={isLinkedByF2S}
            onChange={(e: any) => {
              onCheckBoxAction({
                checked: e.target.checked,
                activity: {
                  link_type: LinkTypes.finish_to_start,
                  start_date: data.start_date,
                  end_date: data.end_date,
                  id: data.id,
                  source_task_id: data.source_task_id,
                  text: data.text,
                  type: data.type
                }
              });
            }}
            data-testid="activitiestable-checkbox-startdate"
          >
            <i>{startDate}</i>
          </Checkbox>
        ) : (
          <i className="mr-2">{startDate}</i>
        )}
      </div>
    );
  }

  const endDate = data?.end_date
    ? DateUtils.format(data.end_date, "MM-DD-YYYY")
    : "";
  if (endDate === "") return "";

  const isLinkedByF2F = linkedActivities.some(
    (activity: any) =>
      activity.id === data.id &&
      activity.link_type === LinkTypes.finish_to_finish
  );

  return (
    <div className="flex w-full justify-end items-center">
      {data.type !== 5 ? (
        <Checkbox
          checked={isLinkedByF2F}
          onChange={(e: any) => {
            onCheckBoxAction({
              checked: e.target.checked,
              activity: {
                link_type: LinkTypes.finish_to_finish,
                start_date: data.start_date,
                end_date: data.end_date,
                id: data.id,
                source_task_id: data.source_task_id,
                text: data.text,
                type: data.type
              }
            });
          }}
          data-testid="activitiestable-checkbox-enddate"
        >
          <i>{endDate}</i>
        </Checkbox>
      ) : (
        <i className="mr-2">{endDate}</i>
      )}
    </div>
  );
}

function ActivitiesTable(props: {
  activitiesTree: any[];
  linkedActivities: any[];
  onCheckBoxAction: (data: any) => void;
  selectedSchedule: any;
  isLoadingScheduleList: boolean;
}) {
  const {
    activitiesTree,
    linkedActivities,
    onCheckBoxAction,
    selectedSchedule,
    isLoadingScheduleList
  } = props;

  const gridRef = useRef<AgGridReact<any>>(null);
  const { tokenContents }: TProjectContext = useContext(ProjectContext);

  const [isGridReady, setGridReady] = useState(false);

  const isGCUser = matchCustomUserProjectRole(
    ECustomUserTypes.AllGCUserRole,
    tokenContents?.role
  );

  const cellWithTaskId = (params: any) => {
    if (params.data.type !== 5) {
      return params.data.source_task_id;
    }

    if (params?.node?.allChildrenCount) {
      return params.data.source_task_id;
    }

    return (
      <Tooltip
        title="You can only link an activity, milestones can't be linked."
        placement="topLeft"
      >
        <div className="flex items-center">
          <div className="flex h-5 w-5 items-center">
            <NoLinkIcon fill="#3B3B3B33" size={18} />
          </div>
          <i className="mr-2">{params.data.source_task_id}</i>
        </div>
      </Tooltip>
    );
  };

  function setNodesCollapseandExpand(id: string, isExpanded: boolean) {
    gridRef.current?.api.forEachNode((node) => {
      if (node.data?.source_task_id === id) {
        // const isExpanded = !node.expanded;
        gridRef.current?.api.setRowNodeExpanded(node, isExpanded);
        // eslint-disable-next-line array-callback-return
        node?.allLeafChildren?.map((leafNode) => {
          gridRef.current?.api.setRowNodeExpanded(
            leafNode,
            leafNode.expanded === isExpanded
              ? leafNode.expanded
              : !leafNode.expanded
          );
        });
      }
    });
  }

  function getContextMenuItems(
    params: GetContextMenuItemsParams
  ): (DefaultMenuItem | MenuItemDef)[] {
    const menuItems: MenuItemDef[] = [
      {
        name: "Expand All",
        action: () => {
          setNodesCollapseandExpand(
            params?.node?.data?.source_task_id ?? "",
            true
          );
        },
        icon: `<img src='${ExpandIconPath}' alt="Expand" />`,
        tooltip: "Expand all child nodes"
      },
      {
        name: "Collapse All",
        action: () => {
          setNodesCollapseandExpand(
            params?.node?.data?.source_task_id ?? "",
            false
          );
        },
        icon: `<img src='${CollapseIconPath}' alt="Collapse" />`,
        tooltip: "Collapse all child nodes"
      }
    ];

    // Only show context menu if there are multiple leaf children
    return (params?.node?.allLeafChildren?.length ?? 0) > 1 ? menuItems : [];
  }

  const isCSVFile = useMemo(() => {
    return getScheduleFileExtension(selectedSchedule, "csv");
  }, [selectedSchedule]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      filter: false,
      sortable: true,
      resizable: true,
      editable: false,
      menuTabs: [],
      getContextMenuItems,
      cellRendererParams: {
        suppressCount: true
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    const colArr: any = [
      {
        colId: "source_task_id",
        field: "source_task_id",
        headerName: "ID",
        headerTooltip: "ID",
        tooltipValueGetter: (params: any) => {
          if (params.data.type === 5 && !params.node?.allChildrenCount) {
            return "";
          }
          return params.value;
        },
        showRowGroup: true,
        filter: true,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: cellWithTaskId
        },
        sort: "desc",
        minWidth: 150
      },
      {
        colId: "critical",
        field: "critical",
        headerName: "",
        headerTooltip: "CRITICAL ACTIVITY",
        headerComponentParams: criticalActivityHeaderComponent,
        cellRenderer: criticalActivityIndicatorCellRenderer,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            return params?.value === true ? "Critical" : "Non Critical";
          },
          comparator: (valA: any, valB: any) => {
            if (valA === valB) return 0;
            return valA > valB ? -1 : 1;
          }
        },
        minWidth: 90,
        maxWidth: 90
      },
      {
        colId: "text",
        field: "text",
        headerName: "Name",
        headerTooltip: "NAME",
        tooltipField: "text",
        filter: true,
        minWidth: 150
      },
      {
        colId: "start_date",
        field: "start_date",
        headerName: "Start Date",
        headerTooltip: "START DATE",
        headerClass: "dateHedderCell",
        cellRenderer: CellWithDateCheckbox,
        cellRendererParams: {
          isStartDate: true
        },
        comparator: DateFilter.comparator,
        minWidth: 150,
        maxWidth: 150,
        valueGetter: ({ data }: any) => {
          return data.start_date ? DateUtils.format(data.start_date) : "";
        },
        tooltipValueGetter: ({ data }: any) => {
          return data.start_date ? DateUtils.format(data.start_date) : "";
        }
      },
      {
        colId: "end_date",
        field: "end_date",
        headerName: "End Date",
        headerTooltip: "END DATE",
        headerClass: "dateHedderCell",
        cellRenderer: CellWithDateCheckbox,
        cellRendererParams: {
          isStartDate: false
        },
        comparator: DateFilter.comparator,
        minWidth: 150,
        maxWidth: 150,
        valueGetter: ({ data }: any) => {
          return data.end_date ? DateUtils.format(data.end_date) : "";
        },
        tooltipValueGetter: ({ data }: any) => {
          return data.end_date ? DateUtils.format(data.end_date) : "";
        }
      },
      {
        colId: "total_slack",
        field: "total_slack",
        headerName: "Activity Float",
        headerTooltip: "ACTIVITY FLOAT",
        cellStyle: (params: any) => {
          if (params?.data?.critical) {
            return { fontWeight: "bold" };
          }
          return { fontWeight: "normal" };
        },
        valueGetter: (params: any) => {
          if (
            params?.data?.total_slack === null ||
            params.data.type === GanttActivityTypes.Wps
          )
            return "";
          const float = Math.floor((params?.data?.total_slack || 0) / 8);
          return `${float} days`;
        },
        tooltipValueGetter: (params: any) => params?.value,
        minWidth: 100,
        maxWidth: 100
      }
    ];
    if (!isGCUser || isCSVFile) {
      return colArr?.filter(
        (col: ColDef) => col.colId !== "critical" && col.colId !== "total_slack"
      );
    }
    return colArr;
  }, [isGCUser, isCSVFile]);

  const gridOptions: GridOptions = {
    defaultColDef,
    suppressCopyRowsToClipboard: true,
    getContextMenuItems
  };

  const getDataPath = useMemo(() => {
    return (data: any) => {
      return data.org;
    };
  }, []);

  useEffect(() => {
    if (isGridReady) {
      gridRef.current?.api.sizeColumnsToFit();
    }
  }, [activitiesTree, isGridReady, selectedSchedule]);

  useEffect(() => {
    if (isGridReady && gridRef.current && linkedActivities) {
      gridRef.current.api.refreshCells({
        columns: ["start_date", "end_date"],
        force: true
      });
    }
  }, [isGridReady, linkedActivities]);

  return (
    <div className="h-full w-full overflow-hidden flex flex-col">
      <div className="py-2 uppercase text-xs flex items-center justify-between">
        <div>List Of Activities</div>
        <CiqAgSearch
          key="feature_activity_linking_search"
          gridRef={gridRef}
          placeholder="Search Activities"
        />
      </div>
      <div className="ag-theme-alpine flex-1 relative">
        {!selectedSchedule && !isLoadingScheduleList && (
          <div className="absolute z-10 top-0 left-0 w-full h-full flex items-center justify-center text-ciq-gray-800 text-opacity-80 text-xs italic">
            Select the Schedule to display Activities. The Schedule will apply
            to the entire project.
          </div>
        )}
        {isLoadingScheduleList || (selectedSchedule && !activitiesTree) ? (
          <div className="absolute z-10 top-0 left-0 w-full h-full flex items-center justify-center">
            <GridLoadingIndicator />
          </div>
        ) : null}
        <AgGridReact<ScheduleTaskType>
          ref={gridRef}
          rowData={activitiesTree}
          columnDefs={columnDefs}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          headerHeight={40}
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api.sizeColumnsToFit();
          }}
          treeData
          rowSelection="single"
          groupDefaultExpanded={-1}
          getDataPath={getDataPath}
          groupDisplayType="custom"
          suppressRowDrag
          suppressMovableColumns
          suppressCellFocus
          suppressDragLeaveHidesColumns
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          tooltipShowMode="whenTruncated"
          suppressLoadingOverlay
          suppressNoRowsOverlay
          context={{ linkedActivities, onCheckBoxAction }}
          excludeChildrenWhenTreeDataFiltering
        />
      </div>
    </div>
  );
}

export default ActivitiesTable;
